/**
 * PartnerDetailPage - Manage one partner API integration.
 *
 * Route: /admin/partners/$partnerId
 *
 * Two of the three controls here reach outside Orchard, so each states its
 * consequence before it is confirmed:
 *
 * - Changing the API user does NOT affect links already delivered. They carry
 *   the previous account inside their token and keep acting as it until the key
 *   version is rotated.
 * - Rotating the key version withdraws every link already delivered. There is no
 *   partial rotation and no way back.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Badge } from '@/common/components/badge'
import { Button } from '@/common/components/button'
import { AlertDialog } from '@/common/components/alert-dialog'
import { Input } from '@/common/components/input'
import { Select, type SelectOption, type SelectOptionGroup } from '@/common/components/select'
import {
  getPartner,
  listEligibleAccounts,
  rotatePartnerKey,
  updatePartner,
} from '@/features/partners/api/partners'
import { AdminOnly } from '@/features/partners/components/AdminOnly'
import { DeliveriesSwitch } from '@/features/partners/components/DeliveriesSwitch'
import { LeadContactEmail } from '@/features/partners/components/LeadContactEmail'
import { NotificationEmail } from '@/features/partners/components/NotificationEmail'

/** Mirrors RESULT_LIMIT in routes/admin/partners/get.eligible-accounts.py. */
const ELIGIBLE_ACCOUNT_LIMIT = 50

export const Route = createFileRoute({
  component: PartnerDetailPage,
})

export function PartnerDetailPage() {
  const { partnerId } = Route.useParams()
  const { token, roles } = useAuth()
  const isAdmin = roles.includes('admin')
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()

  const [choosingAccount, setChoosingAccount] = useState(false)
  const [chosenAccount, setChosenAccount] = useState('')
  // The endpoint returns at most fifty accounts, so without this the picker is
  // the first fifty by login email and an environment with more has names it
  // cannot reach.
  const [accountSearch, setAccountSearch] = useState('')
  const [confirmingDesignation, setConfirmingDesignation] = useState(false)
  const [confirmingRotation, setConfirmingRotation] = useState(false)

  const { data: partner, isLoading } = useQuery({
    queryKey: ['admin', 'partners', partnerId],
    queryFn: () => getPartner(token ?? '', partnerId),
    enabled: Boolean(token) && isAdmin,
  })

  const { data: accounts } = useQuery({
    queryKey: ['admin', 'partners', 'eligible-accounts', accountSearch],
    queryFn: () => listEligibleAccounts(token ?? '', accountSearch || undefined),
    enabled: Boolean(token) && isAdmin,
    placeholderData: (previous) => previous,
  })

  const cancelChoosing = () => {
    setChoosingAccount(false)
    setChosenAccount('')
  }

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] })
  }

  const update = useMutation({
    mutationFn: (changes: { api_user_id: string }) =>
      updatePartner(token ?? '', partnerId, changes),
    onSuccess: () => {
      invalidate()
      cancelChoosing()
      showSuccessToast('Partner updated.')
    },
    onError: (error: unknown) => showErrorToast(getErrorMessage(error)),
  })

  const rotate = useMutation({
    mutationFn: () => rotatePartnerKey(token ?? '', partnerId),
    onSuccess: (updated) => {
      invalidate()
      showSuccessToast(
        `Key version is now ${updated.link_key_version}. Previously delivered links are refused.`,
      )
    },
    onError: (error: unknown) => showErrorToast(getErrorMessage(error)),
  })

  if (!isAdmin) {
    return <AdminOnly title="Partner" />
  }

  if (isLoading || !partner) {
    return (
      <Stack style={{ padding: 24 }}>
        <Text variant="bodySm">Loading partner…</Text>
      </Stack>
    )
  }

  // Grouped rather than one flat list with the reason appended to each label:
  // an account serving another partner cannot be chosen, and saying so once as a
  // group heading reads better than repeating it on every row. They are listed
  // at all, rather than filtered out, so someone looking for a name finds it and
  // an explanation instead of an absence.
  const available: SelectOption[] = []
  const takenElsewhere: SelectOption[] = []
  for (const account of accounts ?? []) {
    const option: SelectOption = {
      value: account.user_id,
      // login_email can be absent; an empty pair of brackets reads like a bug.
      label: account.login_email
        ? `${account.first_name} ${account.last_name} (${account.login_email})`
        : `${account.first_name} ${account.last_name}`,
    }
    if (account.serving_partner_slug && account.serving_partner_slug !== partner.slug) {
      takenElsewhere.push({ ...option, disabled: true })
    } else {
      available.push(option)
    }
  }
  const accountOptions: (SelectOption | SelectOptionGroup)[] =
    takenElsewhere.length > 0
      ? [
          { label: 'Available', options: available },
          { label: 'Already serving another partner', options: takenElsewhere },
        ]
      : available

  // The endpoint caps at fifty and says so in no other way, so a full page is
  // the only evidence that something was left out.
  const isAccountListFull = (accounts?.length ?? 0) >= ELIGIBLE_ACCOUNT_LIMIT

  // Disabled until the choice differs from what is already set: re-confirming
  // the current account would warn about withdrawing links for no change.
  const isAccountChanged = Boolean(chosenAccount) && chosenAccount !== partner.api_user?.user_id

  const currentAccountName = partner.api_user
    ? `${partner.api_user.first_name} ${partner.api_user.last_name}`
    : 'nobody'

  return (
    <Stack gap="24" style={{ padding: 24 }}>
      <Stack gap="8">
        <Link to="/admin/partners">
          <Text variant="bodySm">Back to Partners</Text>
        </Link>
        <Stack direction="row" gap="12" align="center">
          <Text as="h1" variant="h4" weight="bold">
            {partner.display_name}
          </Text>
          <Badge color={partner.ready_to_deliver ? 'success' : 'neutral'}>
            {partner.ready_to_deliver ? 'Delivering' : 'Not delivering'}
          </Badge>
        </Stack>
        <Text variant="bodySm" color="muted">
          {partner.slug} · key version {partner.link_key_version} · {partner.delivered_count}{' '}
          {partner.delivered_count === 1 ? 'property' : 'properties'} delivered
        </Text>
        {partner.blocked_reason && (
          <Text variant="bodySm" color="muted">
            {partner.blocked_reason}
          </Text>
        )}
      </Stack>

      <Stack gap="8">
        <Text as="h2" variant="bodyLg" weight="bold">
          Deliveries
        </Text>
        <Text variant="bodySm" color="muted">
          Switching off stops new properties being queued. Links already delivered keep working.
        </Text>
        <DeliveriesSwitch partner={partner} />
      </Stack>

      <Stack gap="8">
        <Text as="h2" variant="bodyLg" weight="bold">
          API user
        </Text>
        <Text variant="bodySm" color="muted">
          Every offer and question this partner&apos;s links create belongs to this account. It must
          be an active investor, and it can serve only this partner.
        </Text>
        {/* Read state. The picker only appears once someone asks to change it:
            a select sitting open permanently makes the section look like a form
            left half-finished, and puts a live control next to a value nobody
            asked to edit. */}
        {!choosingAccount && (
          <Stack gap="8" align="start">
            <Text variant="bodySm">
              {partner.api_user ? `Currently acting as ${currentAccountName}.` : 'No account chosen yet.'}
            </Text>
            <Button variant="secondary" onClick={() => setChoosingAccount(true)}>
              {partner.api_user ? 'Change API user' : 'Choose an API user'}
            </Button>
          </Stack>
        )}

        {/* Edit state. Confirming is a second step, because the consequence for
            links already delivered is not obvious from the choice itself. */}
        {choosingAccount && (
          <Stack gap="12" align="start">
            {/* The list is capped server-side, so searching is the only way to
                reach an account outside the first page of results. */}
            <Input
              label="Search accounts"
              value={accountSearch}
              onChange={(event) => setAccountSearch(event.target.value)}
              placeholder="Name or email"
              disabled={update.isPending}
              optional
            />
            <Select
              label="Account this partner acts as"
              placeholder="Choose an account…"
              value={chosenAccount}
              onValueChange={setChosenAccount}
              options={accountOptions}
              disabled={update.isPending}
            />
            {isAccountListFull && (
              <Text variant="bodyXs" color="muted">
                Showing the first {accounts?.length} accounts. Search to narrow the list.
              </Text>
            )}
            <Stack direction="row" gap="12">
              <Button
                onClick={() => setConfirmingDesignation(true)}
                disabled={!isAccountChanged || update.isPending}
              >
                Update API user
              </Button>
              <Button variant="secondary" onClick={cancelChoosing} disabled={update.isPending}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>

      <LeadContactEmail partner={partner} />

      <NotificationEmail partner={partner} />

      <Stack gap="8">
        <Text as="h2" variant="bodyLg" weight="bold">
          Link key version
        </Text>
        <Text variant="bodySm" color="muted">
          Rotating is the only way to withdraw links already delivered to this partner.
        </Text>
        <Stack direction="row" gap="12">
          <Button
            variant="outline"
            onClick={() => setConfirmingRotation(true)}
            disabled={rotate.isPending}
          >
            Rotate key version
          </Button>
        </Stack>
      </Stack>

      <AlertDialog
        open={confirmingDesignation}
        onOpenChange={(open) => !open && setConfirmingDesignation(false)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Change this partner&apos;s API user?</AlertDialog.Title>
          <AlertDialog.Description>
            Offers and questions made from now on will belong to the new account. Those already made
            keep their original owner. Links already delivered to {partner.display_name} continue to
            act as {currentAccountName} until you rotate the key version.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={update.isPending}>Cancel</AlertDialog.Cancel>
            <Button
              variant="primary"
              disabled={update.isPending}
              onClick={() => {
                update.mutate({ api_user_id: chosenAccount })
                setConfirmingDesignation(false)
              }}
            >
              Update API user
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      <AlertDialog
        open={confirmingRotation}
        onOpenChange={(open) => !open && setConfirmingRotation(false)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Withdraw every delivered link?</AlertDialog.Title>
          <AlertDialog.Description>
            All {partner.delivered_count}{' '}
            {partner.delivered_count === 1 ? 'link' : 'links'} already delivered to{' '}
            {partner.display_name} will stop working immediately. There is no partial rotation and
            no way back — the partner needs a fresh delivery of anything they still expect to open.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={rotate.isPending}>Cancel</AlertDialog.Cancel>
            <Button
              variant="primary"
              disabled={rotate.isPending}
              onClick={() => {
                rotate.mutate()
                setConfirmingRotation(false)
              }}
            >
              Rotate and withdraw links
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </Stack>
  )
}
